import { useEffect, useState } from "react";
import StockList from "./StockList";
import StockForm from "./StockForm";
import TradingAccountList from "./TradingAccountList";
import TradingAccountForm from "./TradingAccountForm";
import StockTradeList from "./StockTradeList";
import StockTradeForm from "./StockTradeForm";
import StockPositionList from "./StockPositionList";
import StockWatchForm from "./StockWatchForm";
import StockWatchList from "./StockWatchList";
import StockSummary from "./StockSummary";
import StockAlerts from "./StockAlerts";
import StockAnalytics from "./StockAnalytics";
import StockJournal from "./StockJournal";
import NewsTab from "./NewsTab";
import TradeGuide from "./TradeGuide";
import { getMarketIndices } from "./MarketDataService";
import { getPortfolioHealth } from "./StockTradeService";
import "./Stocks.css";

const TABS = ["SUMMARY", "NEWS", "LEARN", "ANALYTICS", "JOURNAL", "ALERTS", "ACCOUNTS", "STOCKS", "TRADES", "POSITIONS", "WATCHLIST"];
const WATCH_INDICES = [
  { key: "NIFTY 50", label: "NIFTY" },
  { key: "NIFTY BANK", label: "BANK" },
  { key: "NIFTY IT", label: "IT" },
  { key: "NIFTY AUTO", label: "AUTO" },
];

const fmtINR = (n) => {
  if (n == null || isNaN(n) || n === 0) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

export default function Stocks() {
  const [tab, setTab] = useState("SUMMARY");
  const [accountsKey, setAccountsKey] = useState(0);
  const [stocksKey, setStocksKey] = useState(0);
  const [tradesKey, setTradesKey] = useState(0);
  const [watchKey, setWatchKey] = useState(0);
  const [tradeFilter, setTradeFilter] = useState({ accountId: "", symbol: "" });
  const [watchData, setWatchData] = useState([]);
  const [heroHealth, setHeroHealth] = useState(null);

  useEffect(() => {
    let timer = null;
    const load = async () => {
      const names = WATCH_INDICES.map((i) => i.key);
      const data = await getMarketIndices(names);
      setWatchData(data || []);
      timer = setTimeout(load, 60000);
    };
    load();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    getPortfolioHealth().then(h => setHeroHealth(h)).catch(() => {});
  }, []);

  const watchMap = new Map(watchData.map((d) => [d.indexName, d]));

  return (
    <div className="card">
      <div className="stocks-hero">
        <div>
          <div className="stocks-hero-title">Trading Desk</div>
          <div className="stocks-hero-sub">
            Track trades, positions, and watchlists with quick CSV imports.
          </div>
        </div>
        <div className="stocks-kpis">
          {heroHealth ? (
            <>
              <span className="stocks-kpi">
                <span className="stocks-kpi-label">Invested</span>
                <span className="stocks-kpi-value">{fmtINR(heroHealth.totalInvested) || "—"}</span>
              </span>
              <span className="stocks-kpi">
                <span className="stocks-kpi-label">Value</span>
                <span className="stocks-kpi-value">{fmtINR(heroHealth.currentValue) || "—"}</span>
              </span>
              <span className={`stocks-kpi ${heroHealth.totalPnl >= 0 ? "stocks-kpi-green" : "stocks-kpi-red"}`}>
                <span className="stocks-kpi-label">P&L</span>
                <span className="stocks-kpi-value">{fmtINR(heroHealth.totalPnl) || "—"}</span>
              </span>
            </>
          ) : (
            <>
              <span className="stocks-kpi stocks-kpi-ghost" />
              <span className="stocks-kpi stocks-kpi-ghost" />
              <span className="stocks-kpi stocks-kpi-ghost" />
            </>
          )}
        </div>
      </div>
      <div className="stocks-ticker">
        <span className="stocks-ticker-label">Watch</span>
        <div className="stocks-ticker-items">
          {WATCH_INDICES.map((item) => {
            const data = watchMap.get(item.key);
            const pct = data?.percentChange ?? null;
            const cls =
              pct === null || pct === undefined
                ? ""
                : Number(pct) >= 0
                ? "stocks-ticker-up"
                : "stocks-ticker-down";
            const label = pct === null || pct === undefined ? "--" : `${Number(pct).toFixed(2)}%`;
            return (
              <span key={item.key} className={`stocks-ticker-item ${cls}`}>
                {item.label} {label}
              </span>
            );
          })}
        </div>
      </div>

      <div className="stocks-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`tab-btn ${tab === t ? "active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "SUMMARY"   && <StockSummary />}
      {tab === "NEWS"      && <NewsTab />}
      {tab === "LEARN"     && <TradeGuide />}
      {tab === "ANALYTICS" && <StockAnalytics />}
      {tab === "JOURNAL"   && <StockJournal />}
      {tab === "ALERTS"    && <StockAlerts />}
      {tab === "ACCOUNTS" && (
        <div className="stocks-section">
          <TradingAccountForm onSaved={() => setAccountsKey((k) => k + 1)} />
          <TradingAccountList refreshKey={accountsKey} />
        </div>
      )}
      {tab === "STOCKS" && (
        <div className="stocks-section">
          <StockForm onSaved={() => setStocksKey((k) => k + 1)} />
          <StockList refreshKey={stocksKey} />
        </div>
      )}
      {tab === "TRADES" && (
        <div className="stocks-section">
          <StockTradeForm onSaved={() => setTradesKey((k) => k + 1)} />
          <StockTradeList
            refreshKey={tradesKey}
            filterAccountId={tradeFilter.accountId}
            filterSymbol={tradeFilter.symbol}
            onClearFilter={() => setTradeFilter({ accountId: "", symbol: "" })}
          />
        </div>
      )}
      {tab === "POSITIONS" && (
        <StockPositionList
          onManageTrades={(accountId, symbol) => {
            setTradeFilter({ accountId, symbol });
            setTab("TRADES");
          }}
        />
      )}
      {tab === "WATCHLIST" && (
        <div className="stocks-section">
          <StockWatchForm onSaved={() => setWatchKey((k) => k + 1)} />
          <StockWatchList refreshKey={watchKey} />
        </div>
      )}
    </div>
  );
}
